/**
 * Onboarding Wizard Page
 * 3-step flow: Connect GitHub -> Analyzing -> Done (auto-applies sensible defaults)
 * Full-width (no AdminLayout sidebar)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { githubApi, GitHubRepo, PatternDiscovery } from '../api/github';
import { projectsApi } from '../api/projects';
import { standardsApi } from '../api/standards';
import { useStatus } from '../contexts/StatusContext';
import type { StandardsCatalog } from '../types';

type Step = 1 | 2 | 3;

const Onboarding: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useStatus();

  const [step, setStep] = useState<Step>(1);
  const [loading, setLoading] = useState(false);

  const oauthError = searchParams.get('error');
  const errorMessages: Record<string, string> = {
    missing_params: 'GitHub did not return authorization data. Please try again.',
    invalid_state: 'Session expired. Please try connecting again.',
    token_exchange_failed: 'Failed to exchange authorization code. Please try again.',
    github_user_failed: 'Could not retrieve your GitHub profile.',
    config_error: 'Server configuration error. Please contact support.',
    server_error: 'An unexpected error occurred. Please try again.',
  };
  const [error, setError] = useState<string | null>(oauthError ? (errorMessages[oauthError] || 'Connection failed. Please try again.') : null);

  const [repos, setRepos] = useState<GitHubRepo[]>([]);
  const [selectedRepo, setSelectedRepo] = useState<GitHubRepo | null>(null);
  const [discoveryProgress, setDiscoveryProgress] = useState<string>('Initializing...');
  const [setupSummary, setSetupSummary] = useState({ patterns: 0, categories: 0 });

  // If redirected back from GitHub OAuth (step=2 in URL), load repos
  const initialStep = parseInt(searchParams.get('step') || '1');
  useEffect(() => {
    if (initialStep === 2 && step === 1) {
      setStep(1);
      loadRepos();
    }
  }, [initialStep]);

  const loadRepos = useCallback(async () => {
    setLoading(true);
    setError(null);
    const result = await githubApi.listRepos();
    const raw = result.data as any;
    const reposData = raw?.data || raw;
    if (result.success && reposData) {
      setRepos(reposData.repos);
    } else {
      setError(result.error || 'Failed to load repositories');
    }
    setLoading(false);
  }, []);

  const handleConnectGitHub = async () => {
    setLoading(true);
    setError(null);
    const result = await githubApi.startOAuth();
    const rawOauth = result.data as any;
    const oauthData = rawOauth?.data || rawOauth;
    if (result.success && oauthData?.authorization_url) {
      sessionStorage.setItem('github_oauth_state', oauthData.state);
      window.location.href = oauthData.authorization_url;
    } else {
      setError(result.error || 'Failed to start OAuth flow');
      setLoading(false);
    }
  };

  const skipToDashboard = () => {
    const _email = user?.email || localStorage.getItem('mindmeld_email') || '';
    localStorage.setItem(`mindmeld_onboarding_skipped_${_email}`, 'true');
    navigate('/dashboard', { replace: true });
  };

  const handleSelectRepo = async (repo: GitHubRepo) => {
    setSelectedRepo(repo);
    setStep(2);
    setError(null);

    const [owner, repoName] = repo.full_name.split('/');

    if (!user?.company_id) {
      setError('No company found. Please contact support.');
      setStep(1);
      return;
    }

    const createResult = await projectsApi.create({
      name: repoName,
      description: `Connected from GitHub: ${repo.full_name}`,
      companyId: user.company_id,
      repoUrl: repo.html_url,
    });

    const projectData = (createResult.data as any)?.data?.Records?.[0] || (createResult.data as any)?.Records?.[0];
    if (!createResult.success || !projectData?.project_id) {
      setError((createResult.data as any)?.message || createResult.error || 'Failed to create project');
      setStep(1);
      return;
    }

    const realProjectId = projectData.project_id;

    const progressMessages = [
      'Analyzing repository structure...',
      'Scanning languages and frameworks...',
      'Detecting testing patterns...',
      'Checking CI/CD configuration...',
      'Building recommendations...'
    ];

    let msgIdx = 0;
    const interval = setInterval(() => {
      msgIdx = Math.min(msgIdx + 1, progressMessages.length - 1);
      setDiscoveryProgress(progressMessages[msgIdx]);
    }, 2000);

    const result = await githubApi.discoverPatterns(realProjectId, owner, repoName, repo.default_branch);
    clearInterval(interval);

    const rawDiscover = result.data as any;
    const discoverData = rawDiscover?.data || rawDiscover;

    let approvedCount = 0;
    if (result.success && discoverData?.discoveries) {
      // Auto-approve patterns with >= 70% confidence
      const approvalList = discoverData.discoveries.map((d: PatternDiscovery, idx: number) => ({
        discovery_id: d.discovery_id || `disc_${idx}`,
        approved: d.confidence >= 0.7
      }));
      approvedCount = approvalList.filter((a: { approved: boolean }) => a.approved).length;

      await githubApi.reviewPatterns(realProjectId, approvalList);
    }

    // Auto-enable recommended standards based on discovered tech stack
    const techStack = (discoverData?.discoveries || [])
      .filter((d: PatternDiscovery) => d.discovery_type === 'tech_stack')
      .map((d: PatternDiscovery) => d.pattern_name);

    let enabledCount = 0;
    const catalogResult = await standardsApi.getCatalog(techStack.length > 0 ? techStack.join(',') : undefined);
    const rawCatalog = catalogResult.data as any;
    const catalogData = rawCatalog?.data || rawCatalog;
    if (catalogResult.success && catalogData) {
      const recs = catalogData.recommendations || [];
      const enabledCategories: Record<string, boolean> = {};
      if (catalogData.categories) {
        Object.keys(catalogData.categories).forEach((cat: string) => {
          enabledCategories[cat] = recs.includes(cat);
        });
      }
      enabledCount = Object.values(enabledCategories).filter(Boolean).length;

      await standardsApi.updateProjectStandards(realProjectId, {
        enabled_categories: enabledCategories
      });
    }

    setSetupSummary({ patterns: approvedCount, categories: enabledCount });
    setStep(3);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">MindMeld Setup</h1>
            <p className="text-sm text-gray-500">
              Welcome{user?.display_name ? `, ${user.display_name}` : ''}! Let's get you started.
            </p>
          </div>
          <div className="flex items-center space-x-2">
            {[1, 2, 3].map((s) => (
              <div
                key={s}
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  s === step
                    ? 'bg-mindmeld-primary text-white'
                    : s < step
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-200 text-gray-500'
                }`}
              >
                {s < step ? '✓' : s}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="max-w-4xl mx-auto px-6 mt-4">
          <div className="bg-red-50 border border-red-200 rounded-md p-4 text-red-700 text-sm">
            {error}
          </div>
        </div>
      )}

      {/* Step content */}
      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Step 1: Connect GitHub + Select Repo */}
        {step === 1 && repos.length === 0 && (
          <div className="text-center py-16">
            <div className="text-6xl mb-6">
              <svg className="w-16 h-16 mx-auto text-gray-800" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Connect a repository</h2>
            <p className="text-gray-600 mb-8 max-w-md mx-auto">
              MindMeld will analyze your repo and automatically configure the right standards for your stack.
            </p>
            <button
              onClick={handleConnectGitHub}
              disabled={loading}
              className="px-8 py-3 bg-gray-900 text-white rounded-lg font-medium hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center space-x-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Connecting...</span>
                </>
              ) : (
                <span>Connect GitHub</span>
              )}
            </button>
            <div className="mt-6">
              <button
                onClick={skipToDashboard}
                className="text-sm text-gray-500 hover:text-gray-700 underline"
              >
                Skip — I'll connect later
              </button>
            </div>
          </div>
        )}

        {/* Step 1 continued: Repo selection (after GitHub OAuth) */}
        {step === 1 && repos.length > 0 && (
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Select a repository</h2>
            <p className="text-gray-600 mb-6">
              Pick a repo and we'll auto-configure everything.
            </p>
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-mindmeld-primary mx-auto mb-3"></div>
                <p className="text-gray-500">Loading repositories...</p>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Repository</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Language</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {repos.map((repo) => (
                      <tr key={repo.full_name} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div>
                            <div className="font-medium text-gray-900">{repo.name}</div>
                            <div className="text-sm text-gray-500">{repo.full_name}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {repo.language || '-'}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <button
                            onClick={() => handleSelectRepo(repo)}
                            className="px-3 py-1.5 text-sm font-medium text-white bg-mindmeld-primary rounded-md hover:bg-blue-700"
                          >
                            Connect
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {repos.length === 0 && !loading && (
                  <div className="text-center py-8 text-gray-500">
                    No repositories found. Make sure your GitHub token has repo access.
                  </div>
                )}
              </div>
            )}
            <div className="mt-6 text-center">
              <button
                onClick={skipToDashboard}
                className="text-sm text-gray-500 hover:text-gray-700 underline"
              >
                Skip — I'll connect later
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Analyzing (auto-configures everything) */}
        {step === 2 && (
          <div className="text-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-mindmeld-primary mx-auto mb-6"></div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Setting up {selectedRepo?.name}...</h2>
            <p className="text-gray-600">{discoveryProgress}</p>
            <p className="text-xs text-gray-400 mt-4">Discovering patterns and configuring standards automatically</p>
          </div>
        )}

        {/* Step 3: Done */}
        {step === 3 && (
          <div className="text-center py-12">
            <div className="text-6xl mb-6">&#127881;</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">You're all set!</h2>
            <p className="text-gray-600 mb-8">
              {selectedRepo?.name} is configured and ready to go.
            </p>

            <div className="max-w-md mx-auto text-left space-y-3 mb-8">
              <div className="flex items-center bg-green-50 rounded-md p-3">
                <span className="text-green-500 mr-3 text-lg">&#10003;</span>
                <span className="text-gray-700">Repository connected: {selectedRepo?.full_name}</span>
              </div>
              <div className="flex items-center bg-green-50 rounded-md p-3">
                <span className="text-green-500 mr-3 text-lg">&#10003;</span>
                <span className="text-gray-700">{setupSummary.patterns} patterns auto-approved</span>
              </div>
              <div className="flex items-center bg-green-50 rounded-md p-3">
                <span className="text-green-500 mr-3 text-lg">&#10003;</span>
                <span className="text-gray-700">{setupSummary.categories} standard categories enabled</span>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-6 mb-8 max-w-md mx-auto text-left">
              <h3 className="font-semibold text-gray-900 mb-3">Install the CLI to start</h3>
              <div className="space-y-2 text-sm">
                <div className="font-mono bg-gray-100 p-2 rounded text-gray-800">
                  npm install -g @equilateral_ai/mindmeld
                </div>
                <div className="font-mono bg-gray-100 p-2 rounded text-gray-800">
                  mindmeld init
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-3">
                Standards are automatically injected into every AI coding session.
              </p>
            </div>

            <button
              onClick={skipToDashboard}
              className="px-8 py-3 bg-mindmeld-primary text-white rounded-lg font-medium hover:bg-blue-700"
            >
              Go to Dashboard
            </button>

            <div className="mt-4 space-x-4">
              <Link to="/standards" className="text-sm text-mindmeld-primary hover:underline">
                Review Standards
              </Link>
              <Link to="/projects" className="text-sm text-mindmeld-primary hover:underline">
                View Projects
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Onboarding;
